/**
 * Minimal fetch wrapper with cookie credentials and error unwrapping.
 *
 * All requests are credentialed so the session cookie set by POST /api/auth/login
 * is sent on subsequent requests — both in dev (Vite proxy → same origin) and
 * in production behind a CWP same-origin reverse proxy. Cross-subdomain
 * deployments require VITE_API_BASE to be an absolute URL and appropriate
 * CORS / cookie settings on the API.
 */

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(message: string, status: number, code: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** User-facing copy for an HTTP 403 — the backend now rejects writes the
 *  current role isn't allowed to make. Kept generic so it reads sensibly for
 *  any forbidden action (create/edit/delete/complete). */
export const FORBIDDEN_MESSAGE =
  "You don't have permission to do that. Ask an administrator if you need access.";

/** Whether an error is an API 403 (forbidden by backend role/permission checks). */
export function isForbidden(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403;
}

/**
 * Map any thrown value to a user-friendly message. Forbidden (403) responses
 * get the clear {@link FORBIDDEN_MESSAGE} so blocked actions don't look broken;
 * other API/JS errors fall back to their own message, then `fallback`.
 */
export function apiErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  if (isForbidden(err)) return FORBIDDEN_MESSAGE;
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
};

export async function apiRequest<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = options;
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    credentials: 'include',
    headers: body != null ? { 'Content-Type': 'application/json' } : undefined,
    body: body != null ? JSON.stringify(body) : undefined,
    signal,
  });

  const text = await res.text();
  const json = text.length > 0 ? (JSON.parse(text) as unknown) : null;

  if (!res.ok) {
    const errShape = (json as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error;
    throw new ApiError(
      errShape?.message ?? `Request failed (${res.status})`,
      res.status,
      errShape?.code ?? 'REQUEST_FAILED',
      errShape?.details,
    );
  }

  return json as T;
}

/**
 * Submit a `multipart/form-data` POST to the API. Used for file uploads where
 * the browser must set its own `Content-Type` boundary, so `apiRequest` (which
 * always serializes to JSON) can't be reused.
 */
export async function apiUpload<T = unknown>(
  path: string,
  formData: FormData,
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  const text = await res.text();
  const json = text.length > 0 ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const errShape = (json as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error;
    throw new ApiError(
      errShape?.message ?? `Upload failed (${res.status})`,
      res.status,
      errShape?.code ?? 'UPLOAD_FAILED',
      errShape?.details,
    );
  }
  return json as T;
}
